import { useEffect, useMemo, useRef, useState } from 'react'
import { GraphQL } from '@guestgoodie/graphql'

import QRCodeStyling, {
  CornerDotType,
  CornerSquareType,
  DotType,
  Options,
  ShapeType
} from 'qr-code-styling'

import { defaultColors, useBranding } from '@/features/branding'

export interface Styling {
  shape: ShapeType
  color: string
  withLogo: boolean
  withText: boolean
  cornerSquareType: CornerSquareType
  cornerDotType: CornerDotType
  dotType: DotType
}

export interface QrCodeContent {
  text?: string | null
  url: string
}

let orgFontWasLoaded: string | false = false

function useFont(brandingFont?: GraphQL.FileFragmentFragment | null) {
  return useMemo(() => {
    if (orgFontWasLoaded === brandingFont?.remoteUrl) {
      return Promise.resolve('50px OrganizationFont')
    }

    if (brandingFont) {
      const fontUrl = `url(${brandingFont.remoteUrl})`
      const font = new FontFace('OrganizationFont', fontUrl)

      return font.load().then(() => {
        orgFontWasLoaded = brandingFont.remoteUrl

        return '40px OrganizationFont'
      })
    }

    return Promise.resolve('40px Lato')
  }, [brandingFont])
}

function useBaseImage(logo: GraphQL.FileFragmentFragment) {
  const imageRef = useRef<HTMLImageElement>(null)

  return useMemo(() => {
    if (imageRef.current && imageRef.current.src === logo.remoteUrl) {
      return Promise.resolve(imageRef.current)
    }

    const imageObj = new Image()

    return new Promise<HTMLImageElement>(resolve => {
      imageObj.onload = function () {
        imageRef.current = imageObj

        resolve(imageObj)
      }

      imageObj.setAttribute('crossOrigin', 'anonymous')
      imageObj.src = logo.remoteUrl
    })
  }, [logo.remoteUrl])
}

function contentCacheKey(content: QrCodeContent, withText: boolean) {
  return `${content.url}_${content.text}_${withText}`
}

function useImages(contents: QrCodeContent[], withText: boolean) {
  const { branding } = useBranding()

  const logo = branding.logo
  const foreground = branding.colors.navForeground
  const background = branding.colors.navBackground
  const txt = branding.colors.txtRegular

  const fontPromise = useFont(branding.fontRegular)
  const imagePromise = useBaseImage(branding.logo)

  const [images, setImages] = useState<Record<string, string>>({})

  useEffect(() => {
    Promise.all([fontPromise, imagePromise]).then(([font, image]) => {
      contents.forEach(function (content) {
        const cacheKey = contentCacheKey(content, withText)

        const text = content.text

        if (cacheKey in images) return images[cacheKey]

        const height = text ? 250 : 200
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')
        canvas.width = 200
        canvas.height = height

        if (!context) return

        const radius = 10
        context.beginPath()
        context.moveTo(radius, 0)
        context.lineTo(200 - radius, 0)
        context.arcTo(200, 0, 200, radius, radius)
        context.lineTo(200, height - radius)
        context.arcTo(200, height, 200 - radius, height, radius)
        context.lineTo(radius, height)
        context.arcTo(0, height, 0, height - radius, radius)
        context.lineTo(0, radius)
        context.arcTo(0, 0, radius, 0, radius)
        context.closePath()
        context.clip()

        // Fill black background
        context.fillStyle = background ?? 'transparent'
        context.fillRect(0, 0, 200, height)

        // Draw image
        context.drawImage(image, 0, 0, 200, 200)

        if (withText && text) {
          context.font = font
          context.fillStyle = foreground ?? txt ?? defaultColors.txtRegular

          context.textAlign = 'center'
          context.fillText(text, 100, 230, 200)
        }

        const dataURL = canvas.toDataURL()

        setImages(cache => {
          const newCache = { ...cache }

          newCache[cacheKey] = dataURL

          return newCache
        })
      })
    })
  }, [
    txt,
    fontPromise,
    imagePromise,
    images,
    setImages,
    contents,
    background,
    foreground,
    withText
  ])

  return useMemo(() => {
    return contents.map(function (content) {
      const cacheKey = contentCacheKey(content, withText)

      return images[cacheKey] ?? logo.remoteUrl
    })
  }, [images, contents, logo.remoteUrl, withText])
}

export function useQrCodes(contents: QrCodeContent[], styling: Styling) {
  const images = useImages(contents, styling.withText)

  const config = useMemo(() => {
    return {
      width: 800,
      height: 800,
      type: 'svg',
      dotsOptions: {
        color: styling.color,
        type: styling.dotType
      },
      imageOptions: {
        margin: 20
      },
      cornersSquareOptions: {
        color: styling.color,
        type: styling.cornerSquareType
      },
      cornersDotOptions: {
        color: styling.color,
        type: styling.cornerDotType
      },
      backgroundOptions: {
        color: 'transparent'
      },
      shape: styling.shape
    } as Options
  }, [styling])

  return useMemo(() => {
    return contents.map(function ({ url }, index) {
      return new QRCodeStyling({
        ...config,
        data: url,
        image: styling.withLogo ? images[index] : undefined
      })
    })
  }, [contents, images, config, styling.withLogo])
}
